import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../api/http';
import { Notice, errorMessage } from '../../components/common';
import { t, type MessageKey } from '../../i18n';
import { formatMinor } from '../../lib/money';

interface Waiting {
  documentsToReview?: number;
  certificateRequests?: number;
  studentLeave?: number;
  staffLeave?: number;
  openApplications?: number;
  failedMessages?: number;
}
interface SchoolCards {
  schoolId: string;
  name: string;
  currencyCode: string;
  students?: { active: number };
  attendance?: {
    marked: number;
    present: number;
    absent: number;
    late: number;
    sectionsSubmitted: number;
    sectionsTotal: number;
  };
  fees?: { collectedThisMonthMinor: number; outstandingMinor: number; overdueMinor: number };
  waiting?: Waiting;
}
interface Overview {
  unreadNotifications: number;
  schools: SchoolCards[];
}

const WAITING: [keyof Waiting, MessageKey, string][] = [
  ['documentsToReview', 'dash.documentsToReview', '/documents'],
  ['certificateRequests', 'dash.certificateRequests', '/certificates'],
  ['studentLeave', 'dash.studentLeave', '/attendance'],
  ['staffLeave', 'dash.staffLeave', '/hr'],
  ['openApplications', 'dash.openApplications', '/admissions'],
  ['failedMessages', 'dash.failedMessages', '/notices'],
];

const Card = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
    <h3 className="text-sm font-medium text-slate-600">{title}</h3>
    <div className="mt-1">{children}</div>
  </div>
);

/** What needs attention at each school, limited to what the signed-in user's permissions allow. */
export function DashboardCards() {
  const overview = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<Overview>('/dashboard'),
  });
  if (overview.isPending) return <p role="status">{t('common.loading')}</p>;
  if (overview.isError) return <Notice kind="error">{errorMessage(overview.error)}</Notice>;
  const { unreadNotifications, schools } = overview.data;

  return (
    <div className="space-y-6">
      {unreadNotifications > 0 && (
        <p className="rounded bg-brand-50 p-3 text-sm">
          {t('dash.unread', { n: unreadNotifications })}{' '}
          <Link to="/inbox" className="font-medium text-brand-700 underline">
            {t('dash.inbox')}
          </Link>
        </p>
      )}
      {schools.length === 0 && <p className="text-sm text-slate-600">{t('dash.noSchools')}</p>}
      {schools.map((s) => {
        const waiting = WAITING.filter(([key]) => s.waiting?.[key] !== undefined);
        return (
          <section key={s.schoolId} aria-label={s.name} className="space-y-3">
            <h2 className="text-lg font-semibold">{s.name}</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {s.students && (
                <Card title={t('dash.students')}>
                  <p className="text-3xl font-bold">{s.students.active}</p>
                </Card>
              )}
              {s.attendance && (
                <Card title={t('dash.attendance')}>
                  <p className="text-sm">
                    {t('dash.attendanceLine', {
                      marked: s.attendance.marked,
                      present: s.attendance.present,
                      absent: s.attendance.absent,
                      late: s.attendance.late,
                    })}
                  </p>
                  <p className="text-xs text-slate-600">
                    {t('dash.registers', {
                      done: s.attendance.sectionsSubmitted,
                      total: s.attendance.sectionsTotal,
                    })}
                  </p>
                </Card>
              )}
              {s.fees && (
                <Card title={t('dash.fees')}>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt>{t('dash.collected')}</dt>
                      <dd className="font-semibold">
                        {formatMinor(s.fees.collectedThisMonthMinor, s.currencyCode)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt>{t('dash.outstanding')}</dt>
                      <dd className="font-semibold">
                        {formatMinor(s.fees.outstandingMinor, s.currencyCode)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt>{t('dash.overdue')}</dt>
                      <dd className="font-semibold text-red-700">
                        {formatMinor(s.fees.overdueMinor, s.currencyCode)}
                      </dd>
                    </div>
                  </dl>
                </Card>
              )}
            </div>
            {s.waiting && (
              <Card title={t('dash.waiting')}>
                {waiting.every(([key]) => !s.waiting![key]) ? (
                  <p className="text-sm text-slate-600">{t('dash.nothingWaiting')}</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {waiting
                      .filter(([key]) => s.waiting![key])
                      .map(([key, label, to]) => (
                        <li key={key} className="flex justify-between gap-2">
                          <Link to={to} className="text-brand-700 underline">
                            {t(label)}
                          </Link>
                          <span className="font-semibold">{s.waiting![key]}</span>
                        </li>
                      ))}
                  </ul>
                )}
              </Card>
            )}
          </section>
        );
      })}
    </div>
  );
}
